import { useCallback, useState } from "react"
import { SortConfig } from "@hydrologiq/shared/types/pagination"
// @ts-ignore - Will be available after GraphQL codegen
import { GetCustomersDocument, GetCustomersQuery, GetCustomersQueryVariables } from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { DEFAULT_PAGE_SIZE } from "@hydrologiq/shared/config"

/**
 * Hook for fetching paginated customers with metrics from customer_list_view
 *
 * Provides pagination, sorting functionality.
 * Metrics are pre-calculated in the database view (financial year scope).
 */
export function useCustomersPaginated() {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sort, setSort] = useState<SortConfig | null>(null)

  // Apollo query with current parameters
  const { data, loading, error, refetch } = useQueryWithEndpoint<GetCustomersQuery, GetCustomersQueryVariables>(
    GetCustomersDocument,
    {
      variables: {
        page: currentPage,
        perPage: pageSize,
        sortBy: sort?.field || null,
        sortDirection: sort?.direction || "asc",
      },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    },
    "primary"
  )

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  // Handle page size change
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size)
    setCurrentPage(1) // Reset to first page
  }, [])

  // Handle sort change
  const handleSortChange = useCallback((newSort: SortConfig | null) => {
    setSort(newSort)
    setCurrentPage(1) // Reset to first page when sorting changes
  }, [])

  // Handle refetch
  const handleRefetch = useCallback(() => {
    refetch()
  }, [refetch])

  return {
    customers: data?.customers?.data || [],
    pagination: data?.customers?.meta,
    loading,
    error,
    sort: sort,
    fetchPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    setSort: handleSortChange,
    refetch: handleRefetch,
  }
}
